import type { CalendarException } from "../api-contracts/operating-calendar.js";
import type { Holiday } from "../api-contracts/organization.js";

// ── Carregar o catálogo é IDEMPOTENTE ──────────────────────────────────────────
// O gestor carrega os feriados uma vez por ano (research). Ele vai, mais cedo ou mais tarde,
// carregar o mesmo ano duas vezes — por engano, por dúvida, ou porque a fábrica ganhou um feriado
// municipal no meio do caminho. Nas três vezes o resultado tem de ser o mesmo calendário.
// Duas garantias, e a segunda é a que se perde por descuido:
//   1. DATA JÁ PRESENTE NÃO DUPLICA. Não é preferência de organização: `OperatingCalendarSchema`
//      recusa duas exceções na mesma data, e o `LEFT JOIN` de `day_ex` no SQL assume o mesmo. Um
//      merge que concatena produziria um payload que o próprio backend rejeita.
//   2. MOTIVO EDITADO À MÃO SOBREVIVE. Se o gestor trocou "Natal" por "Natal — parada de 3 dias",
//      recarregar o catálogo não pode devolvê-lo a "Natal". Reescrever a anotação de alguém é pior
//      que não carregar nada: o dado continua lá, só que o que ele DIZIA sumiu.
// Função pura e compartilhada de propósito: a interface a usa para montar a lista de revisão, e o
// resultado é o que vai no PUT. Uma segunda implementação do merge na tela seria a que decide o que
// o gestor vê antes de confirmar — e divergir dela é divergir no ponto onde a confiança se forma.

export interface HolidayMergeResult {
  /** O conjunto final, ordenado por data e com no máximo uma exceção por data. */
  exceptions: CalendarException[];
  /** Datas que entraram agora. É o número que a tela mostra como "N feriados serão adicionados". */
  added: string[];
  /** Datas do catálogo que JÁ estavam no calendário — nada muda nelas. */
  unchanged: string[];
}

/**
 * Funde os feriados escolhidos no conjunto de exceções datadas existente.
 *
 * `holidays` já vem filtrado pelo que o gestor manteve marcado — esta função não decide o que
 * carregar, só como fundir. Feriado vira `CalendarException` de `kind: "CLOSED"` comum,
 * indistinguível de um lançamento manual e editável ou removível um a um depois.
 */
export function mergeHolidays(
  existing: CalendarException[],
  holidays: Holiday[]
): HolidayMergeResult {
  const byDate = new Map(existing.map((e) => [e.date, e]));
  const added: string[] = [];
  const unchanged: string[] = [];

  for (const holiday of holidays) {
    if (byDate.has(holiday.date)) {
      // Presente: NÃO toca. Vale mesmo quando a exceção existente é `EXTRA_WINDOW` — se a fábrica
      // declarou uma janela extra em 25/12, ela sabe de algo que o catálogo nacional não sabe, e
      // sobrescrever seria o carregamento decidindo contra uma decisão explícita.
      unchanged.push(holiday.date);
      continue;
    }
    byDate.set(holiday.date, { kind: "CLOSED", date: holiday.date, reason: holiday.name });
    added.push(holiday.date);
  }

  const exceptions = [...byDate.values()].sort((a, b) =>
    a.date < b.date ? -1 : a.date > b.date ? 1 : 0
  );
  return { exceptions, added, unchanged };
}

/** Agrupa exceções por ano civil, do mais recente para o mais antigo. */
export function groupExceptionsByYear(
  exceptions: CalendarException[]
): Array<{ year: string; items: CalendarException[] }> {
  const byYear = new Map<string, CalendarException[]>();
  for (const e of exceptions) {
    const year = e.date.slice(0, 4);
    const bucket = byYear.get(year);
    if (bucket) bucket.push(e);
    else byYear.set(year, [e]);
  }
  return [...byYear.entries()]
    .map(([year, items]) => ({
      year,
      items: items.sort((a, b) => (a.date < b.date ? -1 : 1)),
    }))
    .sort((a, b) => (a.year < b.year ? 1 : -1));
}
